import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminBorderSystem,
  InsertAdminBorderSystemBody,
  UpdateBorderSystemNewImageBody,
  UpdateBorderSystemRemoveImageBody,
} from "./types/borderSystems";

export const useBorderSystemsApi = () => {
  const api = useApi();

  const getAdminBorderSystem = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/systems/${id}`;
      return useSWRMutation<AdminBorderSystem, CustomError, string>(url, (url) =>
        api.get<AdminBorderSystem>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminBorderSystems = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/border/systems", query);
      return useSWRImmutable<Paginated<AdminBorderSystem>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminBorderSystem>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminBorderSystems = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/border/systems", query);
      return api.get<Paginated<AdminBorderSystem>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminBorderSystem = useCallback(
    async (body: InsertAdminBorderSystemBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/systems`;
      const id = await api.post<{ id: string }, InsertAdminBorderSystemBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminBorderSystem = useCallback(
    async (
      systemId: string,
      body: UpdateBorderSystemRemoveImageBody | UpdateBorderSystemNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/border/systems/${systemId}`;
      (await api.put<UpdateBorderSystemRemoveImageBody | UpdateBorderSystemNewImageBody>(url, body),
        mutateCache(["/admin/settings/border/systems"]));
    },
    [api],
  );

  const updateAdminBorderSystemVisibility = useCallback(
    async (systemId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/systems/${systemId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache([`/admin/settings/border/systems`]));
    },
    [api],
  );

  const updateAdminBorderSystemOrder = useCallback(
    async (systemId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/systems/${systemId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order), mutateCache([`/admin/settings/border/systems`]));
    },
    [api],
  );

  const deleteAdminBorderSystem = useCallback(
    async (borderSystemId: string): Promise<void> => {
      const url = `/admin/settings/border/systems/${borderSystemId}`;
      await api.del(url).then(() => {
        mutateCache([`/admin/settings/border/systems`]);
      });
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (borderSystemId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/border/systems/${borderSystemId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  return {
    getAdminBorderSystem,
    useAdminBorderSystems,
    getAdminBorderSystems,
    insertAdminBorderSystem,
    updateAdminBorderSystem,
    updateAdminBorderSystemVisibility,
    updateAdminBorderSystemOrder,
    deleteAdminBorderSystem,
    generateUploadImageUrl,
  };
};
